import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Loader2, Plus, Trash2, GitBranch } from "lucide-react";
import { toast } from "sonner";

/**
 * OrphanAliasEditor — small CRUD tile on `/admin` for the
 * orphan-uni-id → canonical-uni-id map used by `wua-sweep-orphans`.
 * Also shows the current `prompt_history` (last 3 versions of the
 * admissions prompt) so future regressions can be bisected.
 */
export default function OrphanAliasEditor() {
  const [aliases, setAliases] = useState(null);
  const [prompts, setPrompts] = useState(null);
  const [fromId, setFromId] = useState("");
  const [toId, setToId] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [a, p] = await Promise.all([
        api.get("/admin/orphan-aliases"),
        api.get("/admin/prompt-history").catch(() => ({ data: { items: [] } })),
      ]);
      setAliases(a.data.items || []);
      setPrompts(p.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!fromId.trim() || !toId.trim()) {
      toast.error("Both fields are required");
      return;
    }
    setBusy(true);
    try {
      await api.post("/admin/orphan-aliases", { from: fromId.trim(), to: toId.trim() });
      toast.success(`Alias saved: ${fromId} → ${toId}`);
      setFromId(""); setToId("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const del = async (from) => {
    if (!window.confirm(`Remove alias '${from}'? Already-remapped degrees stay put.`)) return;
    try {
      await api.delete(`/admin/orphan-aliases/${encodeURIComponent(from)}`);
      toast.success("Removed");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div
      data-testid="admin-orphan-alias-card"
      className="p-6 border border-stone-300 rounded-xl bg-white"
    >
      <div className="flex items-start gap-2 mb-3">
        <GitBranch className="h-5 w-5 text-stone-800 mt-0.5" />
        <div>
          <h2 className="font-editorial text-lg">Orphan aliases</h2>
          <p className="text-xs text-stone-500 max-w-xl">
            Redirect degrees pointing at a stale/typo'd university_id to the
            canonical row. `wua-sweep-orphans` applies these hourly.
          </p>
        </div>
      </div>

      {/* Add row */}
      <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-2 mb-3">
        <input
          data-testid="orphan-alias-from-input"
          value={fromId}
          onChange={(e) => setFromId(e.target.value)}
          placeholder="Orphan id (e.g. uk-city)"
          className="border border-stone-300 rounded px-2 py-1.5 text-sm font-mono"
        />
        <input
          data-testid="orphan-alias-to-input"
          value={toId}
          onChange={(e) => setToId(e.target.value)}
          placeholder="Canonical id (e.g. uk-city-st-georges)"
          className="border border-stone-300 rounded px-2 py-1.5 text-sm font-mono"
        />
        <button
          data-testid="orphan-alias-add-btn"
          onClick={add}
          disabled={busy}
          className="inline-flex items-center gap-1 rounded border border-stone-800 bg-stone-900 hover:bg-stone-800 text-white text-xs font-underground-sc tracking-wider px-3 py-1.5 disabled:opacity-50"
        >
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
          Save
        </button>
      </div>

      {/* Existing aliases */}
      {aliases === null ? (
        <div className="text-xs text-stone-500 flex items-center gap-2">
          <Loader2 className="h-3 w-3 animate-spin" /> Loading…
        </div>
      ) : aliases.length === 0 ? (
        <div className="text-xs text-stone-400 italic">No aliases defined yet.</div>
      ) : (
        <ul className="divide-y divide-stone-100 border border-stone-200 rounded" data-testid="orphan-alias-list">
          {aliases.map((a) => (
            <li key={a.from} className="flex items-center justify-between gap-2 px-3 py-1.5 text-xs hover:bg-stone-50">
              <span className="font-mono">
                <span className="text-stone-500">{a.from}</span>
                <span className="text-stone-300 mx-2">→</span>
                <span className="text-stone-800">{a.to}</span>
              </span>
              <button
                onClick={() => del(a.from)}
                data-testid={`orphan-alias-del-${a.from}`}
                className="text-stone-400 hover:text-rose-600"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* Prompt history */}
      <div className="mt-5 pt-4 border-t border-stone-200" data-testid="prompt-history-section">
        <div className="flex items-baseline justify-between mb-2">
          <h3 className="font-editorial text-sm">Prompt history</h3>
          <span className="text-[10px] font-mono text-stone-400 uppercase tracking-wider">
            last 3 versions · auto-captured
          </span>
        </div>
        {prompts === null ? (
          <div className="text-xs text-stone-400 italic">Loading…</div>
        ) : prompts.length === 0 ? (
          <div className="text-xs text-stone-400 italic">
            No captures yet — first LLM call will snapshot the current prompt.
          </div>
        ) : (
          <ul className="space-y-1.5">
            {prompts.map((p) => (
              <li key={`${p.kind}-${p.version}`} className="flex items-center gap-3 text-[11px] font-mono">
                <span className="rounded border border-stone-200 bg-stone-50 px-1.5 py-0.5">
                  v{p.version}
                </span>
                <span className="text-stone-500">{p.hash}</span>
                <span className="text-stone-400" title={p.captured_at}>
                  {new Date(p.captured_at).toLocaleDateString(undefined, { day:"numeric", month:"short", year:"numeric" })}
                </span>
                <span className="text-stone-400 truncate flex-1">
                  {p.kind} · {(p.text || "").length} chars
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
